/**
 * Shared contract for immutable investigation report artifacts.
 * Titles, type labels, subject identifiers, and download filenames stay
 * consistent across Workbench, Analysis tab, and Case Detail.
 */

export const REPORT_TYPES = {
    autoInvestigate: 'auto_investigate_html',
    followTheMoney: 'follow_the_money_html',
    holistic: 'holistic_html',
    aiGraphSummary: 'ai_graph_summary_html',
} as const;

export type InvestigationReportType =
    (typeof REPORT_TYPES)[keyof typeof REPORT_TYPES];

export const REPORT_TYPE_LABELS: Record<string, string> = {
    [REPORT_TYPES.autoInvestigate]: 'Targeted Node Intelligence',
    [REPORT_TYPES.followTheMoney]: 'Follow the Money',
    [REPORT_TYPES.holistic]: 'Holistic Multi-Node Trace',
    [REPORT_TYPES.aiGraphSummary]: 'AI Graph Summary',
    // Legacy / alternate keys that may appear in older UI surfaces
    auto_investigate: 'Targeted Node Intelligence',
    follow_the_money: 'Follow the Money',
    holistic: 'Holistic Multi-Node Trace',
};

export interface ReportSubject {
    chain?: string | null;
    address?: string | null;
    addresses?: string[] | null;
}

/** Shorten an address for titles / list chips: TAbcdefg…wxyz */
export function shortAddress(address: string | null | undefined, head = 8, tail = 4): string {
    if (!address) return 'Unknown';
    if (address.length <= head + tail + 1) return address;
    return `${address.slice(0, head)}…${address.slice(-tail)}`;
}

/**
 * Compact subject identifier for list UI.
 * Single address → shortened address.
 * Holistic seeds → first short address + "+N" when more remain.
 */
export function subjectIdentifier(meta?: ReportSubject | Record<string, unknown> | null): string | null {
    if (!meta) return null;
    const addresses = Array.isArray(meta.addresses)
        ? (meta.addresses as unknown[]).filter((a): a is string => typeof a === 'string' && a.length > 0)
        : [];
    if (addresses.length > 0) {
        const first = shortAddress(addresses[0]);
        const extra = addresses.length - 1;
        return extra > 0 ? `${first} +${extra}` : first;
    }
    const address = typeof meta.address === 'string' ? meta.address : null;
    return address ? shortAddress(address) : null;
}

export function reportTypeLabel(reportType: string | null | undefined): string {
    if (!reportType) return 'Report';
    if (REPORT_TYPE_LABELS[reportType]) return REPORT_TYPE_LABELS[reportType];
    return reportType.replace(/_html$/, '').replace(/_/g, ' ');
}

function chainPrefix(chain?: string | null): string {
    const c = (chain || '').trim();
    return c ? `${c.toUpperCase()} ` : '';
}

/** Canonical archive title: "Follow the Money — TRX TAbc…1234" */
export function buildReportTitle(
    reportType: InvestigationReportType | string,
    subject: ReportSubject,
): string {
    const label = reportTypeLabel(reportType);
    const chain = chainPrefix(subject.chain);

    if (reportType === REPORT_TYPES.holistic || reportType === 'holistic') {
        const addrs = (subject.addresses || []).filter(Boolean);
        if (addrs.length === 0 && subject.address) {
            return `${label} — ${chain}${shortAddress(subject.address)}`;
        }
        if (addrs.length === 0) {
            return `${label} — ${chain.trim() || 'multi-node'}`.replace(/\s—\s$/, '');
        }
        const first = shortAddress(addrs[0]);
        const extra = addrs.length - 1;
        const id = extra > 0 ? `${first} +${extra}` : first;
        return `${label} — ${chain}${id}`.replace(/\s+/g, ' ').trim();
    }

    const addr = shortAddress(subject.address || subject.addresses?.[0]);
    return `${label} — ${chain}${addr}`.replace(/\s+/g, ' ').trim();
}

/** Safe download basename without extension */
export function buildReportFilename(
    reportType: InvestigationReportType | string,
    subject: ReportSubject,
    when: Date = new Date(),
): string {
    const date = when.toISOString().slice(0, 10);
    const chain = (subject.chain || 'unknown').toString().toUpperCase().replace(/[^A-Z0-9]/g, '');
    const addr = (subject.address || subject.addresses?.[0] || 'graph')
        .toString()
        .slice(0, 12)
        .replace(/[^a-zA-Z0-9]/g, '');

    const prefix: Record<string, string> = {
        [REPORT_TYPES.autoInvestigate]: 'targeted_node',
        [REPORT_TYPES.followTheMoney]: 'follow_money',
        [REPORT_TYPES.holistic]: 'holistic_trace',
        [REPORT_TYPES.aiGraphSummary]: 'ai_graph_summary',
    };

    const stem = prefix[reportType] || reportType.replace(/_html$/, '').replace(/[^a-zA-Z0-9_]/g, '_');
    return `${stem}_${chain || 'NA'}_${addr || 'subject'}_${date}`;
}

/** Pull subject fields from artifact metadata for display */
export function subjectFromMetadata(
    metadata?: Record<string, unknown> | null,
): ReportSubject {
    if (!metadata) return {};
    return {
        chain: typeof metadata.chain === 'string' ? metadata.chain : null,
        address: typeof metadata.address === 'string' ? metadata.address : null,
        addresses: Array.isArray(metadata.addresses)
            ? metadata.addresses.filter((a): a is string => typeof a === 'string')
            : null,
    };
}
